// src/components/grid/GridSettingsModal.tsx
// HANDLES: Comprehensive grid settings including line management, grid properties, and advanced options
import React, { useState, useEffect, useMemo } from 'react';
import { Settings, Grid as GridIcon, Layers, Users, Clock, Edit2, Plus, Trash2, AlertTriangle, Info, Save, X } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import NewLineManagementModal from './NewLineManagementModal';
import { StaffAllocationGrid as StaffGrid } from '../../types/grid';
import { debug } from '../../utils/debug';
interface GridSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  grid: StaffGrid;
  onSaveChanges: (updates: Partial<StaffGrid>) => Promise<void>;
  onDeleteGrid?: () => Promise<void>;
}
const GridSettingsModal: React.FC<GridSettingsModalProps> = ({
  isOpen,
  onClose,
  grid,
  onSaveChanges,
  onDeleteGrid
}) => {
  const [activeTab, setActiveTab] = useState<'general' | 'lines' | 'advanced'>('general');
  const [formData, setFormData] = useState({
    name: grid.name,
    description: grid.description || '',
    defaultClassMinutes: grid.defaultClassMinutes || 70,
    defaultClassMinutesInput: String(grid.defaultClassMinutes || 70)
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [showLineManagement, setShowLineManagement] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showClearAssignmentsConfirm, setShowClearAssignmentsConfirm] = useState(false);
  const [showDeleteSubjectsConfirm, setShowDeleteSubjectsConfirm] = useState(false);
  const [deleteSubjectsType, setDeleteSubjectsType] = useState<'all' | 'selected'>('all');

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: grid.name,
        description: grid.description || '',
        defaultClassMinutes: grid.defaultClassMinutes || 70,
        defaultClassMinutesInput: String(grid.defaultClassMinutes || 70)
      });
      setHasChanges(false);
      setActiveTab('general');
      setShowDeleteConfirm(false);
      setShowClearAssignmentsConfirm(false);
      setShowDeleteSubjectsConfirm(false);
      setDeleteSubjectsType('all');
    }
  }, [isOpen, grid.name, grid.description, grid.defaultClassMinutes]);

  // Track changes
  useEffect(() => {
    const nameChanged = formData.name !== grid.name;
    const descriptionChanged = formData.description !== (grid.description || '');
    const minutesChanged = formData.defaultClassMinutes !== (grid.defaultClassMinutes || 70);
    setHasChanges(nameChanged || descriptionChanged || minutesChanged);
  }, [formData, grid.name, grid.description, grid.defaultClassMinutes]);

  // Calculate grid statistics
  const gridStats = useMemo(() => {
    const totalAssignments = grid.classes.reduce((count, classItem) => {
      return count + Object.values(classItem.lineAssignments || {}).filter(Boolean).length;
    }, 0);
    const totalPossibleAssignments = grid.classes.length * grid.lines.length;
    const assignmentPercentage = totalPossibleAssignments > 0 ? Math.round(totalAssignments / totalPossibleAssignments * 100) : 0;

    // Count conflicts
    const conflictCount = new Set<string>();
    const teacherAssignments = new Map<string, Array<{
      classId: string;
      lineId: string;
    }>>();
    grid.classes.forEach(classItem => {
      Object.entries(classItem.lineAssignments || {}).forEach(([lineId, teacherCode]) => {
        if (teacherCode) {
          if (!teacherAssignments.has(teacherCode)) {
            teacherAssignments.set(teacherCode, []);
          }
          teacherAssignments.get(teacherCode)!.push({
            classId: classItem.id,
            lineId
          });
        }
      });
    });
    teacherAssignments.forEach(assignments => {
      const lineGroups = new Map<string, Array<{
        classId: string;
      }>>();
      assignments.forEach(({
        classId,
        lineId
      }) => {
        if (!lineGroups.has(lineId)) {
          lineGroups.set(lineId, []);
        }
        lineGroups.get(lineId)!.push({
          classId
        });
      });
      lineGroups.forEach(classes => {
        if (classes.length > 1) {
          classes.forEach(({
            classId
          }) => {
            conflictCount.add(classId);
          });
        }
      });
    });
    return {
      totalClasses: grid.classes.length,
      totalLines: grid.lines.length,
      totalAssignments,
      totalPossibleAssignments,
      assignmentPercentage,
      conflicts: conflictCount.size,
      curriculumAreas: grid.curriculumAreas?.length || 0,
      semesters: grid.semesters?.length || 0
    };
  }, [grid]);

  // Convert simple string lines to Line objects for NewLineManagementModal
  const convertLinesToObjects = useMemo(() => {
    if (!grid.lines || !Array.isArray(grid.lines)) return [];
    
    return grid.lines.map((line) => {
      // If it's already an object, use it as is
      if (typeof line === 'object' && line.id) {
        return line;
      }
      
      // If it's a string, convert to object
      const lineId = typeof line === 'string' ? line : line.id || line.name || line;
      
      // Determine semester based on line ID
      let semester: 'semester1' | 'semester2' | 'fullyear' = 'semester1';
      if (['G', 'H', 'I', 'J', 'K', 'L'].includes(lineId) || lineId.includes('S2')) {
        semester = 'semester2';
      }
      
      return {
        id: lineId,
        name: `Line ${lineId}`,
        semester,
        days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
      };
    });
  }, [grid.lines]);

  // Convert studyPeriods to semesters for NewLineManagementModal
  const convertStudyPeriodsToSemesters = useMemo(() => {
    if (!grid.studyPeriods || !Array.isArray(grid.studyPeriods)) {
      return [
        { id: 'semester1', name: 'Semester 1', lineIds: [] },
        { id: 'semester2', name: 'Semester 2', lineIds: [] }
      ];
    }
    
    return grid.studyPeriods.map(period => ({
      id: period.id,
      name: period.name,
      lineIds: period.lines || []
    }));
  }, [grid.studyPeriods]);

  // Handle form changes
  const handleInputChange = (field: 'name' | 'description' | 'defaultClassMinutes', value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle line management changes
  const handleLineManagementSave = async (newLines: any[], newSemesters: any[]) => {
    try {
      debug('🔧 GridSettings: Received line changes from LineManagementModal');
      debug('🔧 GridSettings: New lines:', newLines.length, 'lines');
      debug('🔧 GridSettings: Line IDs:', newLines.map(l => l.id));
      debug('🔧 GridSettings: New semesters:', newSemesters);
      
      // Convert semesters back to studyPeriods format for StaffAllocationGrid
      const studyPeriods = newSemesters.map(semester => ({
        id: semester.id,
        name: semester.name,
        lines: semester.lineIds || []
      }));
      
      const updatePayload = {
        lines: newLines,
        studyPeriods: studyPeriods
      };
      
      debug('🔧 GridSettings: Calling onSaveChanges with payload:', updatePayload);
      await onSaveChanges(updatePayload);
      debug('🔧 GridSettings: Line changes saved successfully');
      setShowLineManagement(false);
    } catch (error) {
      console.error('🔧 GridSettings: Failed to save line changes:', error);
      console.error('🔧 GridSettings: Error details:', error);
      throw error;
    }
  };

  // Save general settings
  const handleSaveGeneral = async () => {
    try {
      const updates: any = {
        name: formData.name.trim(),
        defaultClassMinutes: formData.defaultClassMinutes
      };
      
      // Only include description if it has a value (Firestore doesn't allow undefined)
      const trimmedDescription = formData.description.trim();
      if (trimmedDescription) {
        updates.description = trimmedDescription;
      }
      
      await onSaveChanges(updates);
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to save grid settings:', error);
    }
  };

  // Handle grid deletion
  const handleDeleteGrid = async () => {
    if (!onDeleteGrid) return;
    try {
      await onDeleteGrid();
      onClose();
    } catch (error) {
      console.error('Failed to delete grid:', error);
    }
  };

  // Handle clearing all assignments
  const handleClearAllAssignments = async () => {
    try {
      const clearedClasses = grid.classes.map(classItem => ({
        ...classItem,
        lineAssignments: {}
      }));
      
      await onSaveChanges({
        classes: clearedClasses
      });
      
      setShowClearAssignmentsConfirm(false);
    } catch (error) {
      console.error('Failed to clear assignments:', error);
    }
  };

  // Handle deleting subjects
  const handleDeleteSubjects = async () => {
    try {
      if (deleteSubjectsType === 'all') {
        // Delete all classes
        await onSaveChanges({
          classes: []
        });
      }
      // Note: 'selected' functionality would require additional UI for selection
      
      setShowDeleteSubjectsConfirm(false);
    } catch (error) {
      console.error('Failed to delete subjects:', error);
    }
  };
  const tabs = [{
    id: 'general',
    name: 'General',
    icon: GridIcon
  }, {
    id: 'lines',
    name: 'Lines & Structure',
    icon: Layers
  }, {
    id: 'advanced',
    name: 'Advanced',
    icon: Settings
  }];
  return <>
      <Modal isOpen={isOpen} onClose={onClose} title="Grid Settings" size="2xl" footer={<div className="flex justify-between items-center w-full">
            <div className="text-sm text-ink-500">
              {gridStats.totalClasses} classes • {gridStats.totalLines} lines • {gridStats.assignmentPercentage}% assigned
            </div>
            <div className="flex space-x-3">
              <Button variant="secondary" onClick={onClose}>
                Close
              </Button>
              {activeTab === 'general' && hasChanges && <Button variant="primary" onClick={handleSaveGeneral}>
                  Save Changes
                </Button>}
            </div>
          </div>}>
        <div className="flex min-h-[500px] max-h-[70vh] overflow-hidden">
          {/* Sidebar Navigation */}
          <div className="w-48 flex-shrink-0 border-r border-ink-200 pr-4 pt-2">
            <nav className="space-y-1">
              {tabs.map(tab => {
              const Icon = tab.icon;
              return <button key={tab.id} onClick={() => setActiveTab(tab.id as any)} className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === tab.id ? 'bg-navy-100 text-navy-700 border-navy-200' : 'text-ink-500 hover:text-ink-900 hover:bg-ink-50'}`}>
                    <Icon className="h-4 w-4 mr-3" />
                    {tab.name}
                  </button>;
            })}
            </nav>
          </div>

          {/* Content Area */}
          <div className="flex-1 pl-6 pt-2 overflow-y-auto">
            {activeTab === 'general' && <div className="space-y-6 pb-6">
                <div>
                  <h3 className="text-lg font-medium text-ink-900 mb-4">General Settings</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="gridName" className="block text-sm font-medium text-ink-700">
                        Grid Name
                      </label>
                      <input type="text" id="gridName" value={formData.name} onChange={e => handleInputChange('name', e.target.value)} className="mt-1 block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" placeholder="e.g., 2025 Semester 1 Grid" />
                    </div>

                    <div>
                      <label htmlFor="gridDescription" className="block text-sm font-medium text-ink-700">
                        Description (Optional)
                      </label>
                      <textarea id="gridDescription" rows={3} value={formData.description} onChange={e => handleInputChange('description', e.target.value)} className="mt-1 block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" placeholder="Brief description of this grid..." />
                    </div>

                    <div>
                      <label htmlFor="defaultClassMinutes" className="block text-sm font-medium text-ink-700">
                        Default Class Duration (Minutes)
                      </label>
                      <input 
                        type="number" 
                        id="defaultClassMinutes" 
                        value={formData.defaultClassMinutesInput} 
                        onChange={e => {
                          const value = e.target.value;
                          setFormData(prev => ({
                            ...prev,
                            defaultClassMinutesInput: value,
                            defaultClassMinutes: value === '' ? 70 : (parseInt(value) || prev.defaultClassMinutes)
                          }));
                        }}
                        className="mt-1 block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" 
                        placeholder="70"
                        min="1"
                        max="300"
                      />
                      <p className="mt-1 text-sm text-ink-500">
                        Default duration for each class period in minutes (commonly 70 minutes)
                      </p>
                    </div>
                  </div>
                </div>

                {/* Grid Statistics */}
                <div className="bg-ink-50 rounded-lg p-5">
                  <h4 className="text-base font-medium text-ink-900 mb-4">Grid Statistics</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-ink-500">Total Classes:</span>
                      <span className="font-mono tabular-nums font-medium">{gridStats.totalClasses}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-ink-500">Total Lines:</span>
                      <span className="font-mono tabular-nums font-medium">{gridStats.totalLines}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-ink-500">Assignments Made:</span>
                      <span className="font-mono tabular-nums font-medium">{gridStats.totalAssignments}/{gridStats.totalPossibleAssignments}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-ink-500">Completion:</span>
                      <span className={`font-mono tabular-nums font-medium ${gridStats.assignmentPercentage >= 80 ? 'text-ok-fg' : gridStats.assignmentPercentage >= 50 ? 'text-warn-fg' : 'text-danger-fg'}`}>
                        {gridStats.assignmentPercentage}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-ink-500">Conflicts:</span>
                      <span className={`font-mono tabular-nums font-medium ${gridStats.conflicts > 0 ? 'text-danger-fg' : 'text-ok-fg'}`}>
                        {gridStats.conflicts}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-ink-500">Learning Areas:</span>
                      <span className="font-mono tabular-nums font-medium">{gridStats.curriculumAreas}</span>
                    </div>
                  </div>
                </div>
              </div>}

            {activeTab === 'lines' && <div className="space-y-6 pb-6">
                <div>
                  <h3 className="text-lg font-medium text-ink-900 mb-4">Lines & Structure</h3>
                  
                  <div className="space-y-6">
                    {/* Line Management Section */}
                    <div className="border border-ink-200 rounded-lg p-5">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="text-sm font-medium text-ink-900">Manage Lines</h4>
                          <p className="text-xs text-ink-500 mt-1">
                            Add, remove, or modify grid lines and their semester assignments
                          </p>
                        </div>
                        <Button variant="primary" size="sm" leftIcon={<Edit2 className="h-4 w-4" />} onClick={() => setShowLineManagement(true)}>
                          Manage Lines
                        </Button>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-ink-500">Semester 1 Lines:</span>
                          <span className="font-mono tabular-nums ml-2 font-medium text-navy-600">
                            {grid.lines?.filter(l => l.semester === 'semester1' || ['A', 'B', 'C', 'D', 'E', 'F'].includes(l.id)).length || 0}
                          </span>
                        </div>
                        <div>
                          <span className="text-ink-500">Semester 2 Lines:</span>
                          <span className="font-mono tabular-nums ml-2 font-medium text-ok-fg">
                            {grid.lines?.filter(l => l.semester === 'semester2' || ['G', 'H', 'I', 'J', 'K', 'L'].includes(l.id)).length || 0}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Current Lines Preview */}
                    <div className="border border-ink-200 rounded-lg p-5">
                      <h4 className="text-sm font-medium text-ink-900 mb-3">Current Lines</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <h5 className="text-xs font-medium text-navy-600 uppercase tracking-wide mb-2">Semester 1</h5>
                          <div className="space-y-1">
                            {grid.lines?.filter(l => l.semester === 'semester1' || ['A', 'B', 'C', 'D', 'E', 'F'].includes(l.id)).map(line => <div key={line.id} className="flex items-center space-x-2 text-sm">
                                <span className="font-mono tabular-nums font-bold text-navy-700">{line.id}</span>
                                <span className="text-ink-500 truncate">{line.name || `Line ${line.id}`}</span>
                              </div>)}
                          </div>
                        </div>
                        <div>
                          <h5 className="text-xs font-medium text-ok-fg uppercase tracking-wide mb-2">Semester 2</h5>
                          <div className="space-y-1">
                            {grid.lines?.filter(l => l.semester === 'semester2' || ['G', 'H', 'I', 'J', 'K', 'L'].includes(l.id)).map(line => <div key={line.id} className="flex items-center space-x-2 text-sm">
                                <span className="font-mono tabular-nums font-bold text-ok-fg">{line.id}</span>
                                <span className="text-ink-500 truncate">{line.name || `Line ${line.id}`}</span>
                              </div>)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>}

            {activeTab === 'advanced' && <div className="space-y-6 pb-6">
                <div>
                  <h3 className="text-lg font-medium text-ink-900 mb-4">Advanced Settings</h3>
                  
                  <div className="space-y-6">
                    {/* Export/Import Settings */}
                    <div className="border border-ink-200 rounded-lg p-5">
                      <h4 className="text-sm font-medium text-ink-900 mb-2">Export/Import</h4>
                      <p className="text-xs text-ink-500 mb-3">
                        Export grid data or import from external sources
                      </p>
                      <div className="flex space-x-2">
                        <Button variant="secondary" size="sm">
                          Export Grid Data
                        </Button>
                        <Button variant="secondary" size="sm">
                          Import Subjects
                        </Button>
                      </div>
                    </div>

                    {/* Automation Settings */}
                    <div className="border border-ink-200 rounded-lg p-5">
                      <h4 className="text-sm font-medium text-ink-900 mb-2">Automation (Coming Soon)</h4>
                      <p className="text-xs text-ink-500 mb-3">
                        Configure AI-assisted teacher assignment preferences
                      </p>
                      <div className="space-y-2 text-sm">
                        <label className="flex items-center space-x-2 text-ink-400">
                          <input type="checkbox" disabled className="rounded border-ink-300" />
                          <span>Auto-assign teachers to paired semester lines</span>
                        </label>
                        <label className="flex items-center space-x-2 text-ink-400">
                          <input type="checkbox" disabled className="rounded border-ink-300" />
                          <span>Suggest assignments based on teacher preferences</span>
                        </label>
                        <label className="flex items-center space-x-2 text-ink-400">
                          <input type="checkbox" disabled className="rounded border-ink-300" />
                          <span>Warn before creating conflicts</span>
                        </label>
                      </div>
                    </div>

                    {/* Danger Zone */}
                    <div className="border border-danger-border rounded-lg p-5 bg-danger-bg">
                      <h4 className="text-sm font-medium text-danger-fg mb-2">Danger Zone</h4>
                      <p className="text-xs text-danger-fg mb-3">
                        Destructive actions that cannot be undone. Use with caution.
                      </p>
                      <div className="space-y-3">
                        {/* Subject Management */}
                        <div>
                          <p className="text-xs text-danger-fg mb-2">
                            Delete all subjects from this grid ({grid.classes.length} subjects will be removed)
                          </p>
                          <Button 
                            variant="secondary" 
                            size="sm" 
                            leftIcon={<Trash2 className="h-4 w-4" />} 
                            onClick={() => setShowDeleteSubjectsConfirm(true)} 
                            className="text-danger-fg border-danger-border hover:bg-danger-bg"
                            disabled={grid.classes.length === 0}
                          >
                            Delete All Subjects
                          </Button>
                        </div>
                        <div>
                          <p className="text-xs text-danger-fg mb-2">
                            Clear all teacher assignments from this grid ({gridStats.totalAssignments} assignments will be removed)
                          </p>
                          <Button 
                            variant="secondary" 
                            size="sm" 
                            leftIcon={<X className="h-4 w-4" />} 
                            onClick={() => setShowClearAssignmentsConfirm(true)} 
                            className="text-danger-fg border-danger-border hover:bg-danger-bg"
                            disabled={gridStats.totalAssignments === 0}
                          >
                            Clear All Assignments
                          </Button>
                        </div>
                        {onDeleteGrid && <div>
                          <p className="text-xs text-danger-fg mb-2">
                            Permanently delete this entire grid and all its data
                          </p>
                          <Button variant="secondary" size="sm" leftIcon={<Trash2 className="h-4 w-4" />} onClick={() => setShowDeleteConfirm(true)} className="text-danger-fg border-danger-border hover:bg-danger-bg">
                            Delete Grid
                          </Button>
                        </div>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </Modal>

      {/* Line Management Modal */}
      {showLineManagement && <NewLineManagementModal isOpen={showLineManagement} onClose={() => setShowLineManagement(false)} lines={convertLinesToObjects} semesters={convertStudyPeriodsToSemesters} onSaveChanges={handleLineManagementSave} classCount={grid.classes?.length || 0} />}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && <Modal isOpen={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} title="Delete Grid" footer={<>
              <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleDeleteGrid} className="bg-danger-solid hover:bg-danger-solid">
                Delete Grid
              </Button>
            </>}>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-6 w-6 text-danger-fg flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-medium text-ink-900">
                  Are you sure you want to delete "{grid.name}"?
                </h3>
                <p className="text-sm text-ink-500 mt-2">
                  This will permanently delete:
                </p>
                <ul className="text-sm text-ink-500 mt-1 list-disc pl-5 space-y-1">
                  <li>{gridStats.totalClasses} classes and their configurations</li>
                  <li>{gridStats.totalAssignments} teacher assignments</li>
                  <li>All line definitions and semester structures</li>
                  <li>Grid settings and preferences</li>
                </ul>
                <p className="text-sm text-danger-fg font-medium mt-3">
                  This action cannot be undone.
                </p>
              </div>
            </div>
          </div>
        </Modal>}

      {/* Delete Subjects Confirmation Modal */}
      {showDeleteSubjectsConfirm && (
        <Modal 
          isOpen={showDeleteSubjectsConfirm} 
          onClose={() => setShowDeleteSubjectsConfirm(false)} 
          title="Delete All Subjects" 
          footer={
            <>
              <Button variant="secondary" onClick={() => setShowDeleteSubjectsConfirm(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleDeleteSubjects} className="bg-danger-solid hover:bg-danger-solid">
                Delete All Subjects
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-6 w-6 text-danger-fg flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-medium text-ink-900">
                  Are you sure you want to delete all subjects?
                </h3>
                <p className="text-sm text-ink-500 mt-2">
                  This will permanently remove all subjects from "{grid.name}":
                </p>
                <ul className="text-sm text-ink-500 mt-1 list-disc pl-5 space-y-1">
                  <li>{grid.classes.length} subjects will be permanently deleted</li>
                  <li>All teacher assignments will be removed</li>
                  <li>All class scheduling information will be lost</li>
                  <li>Line-subject mappings will remain but will be orphaned</li>
                </ul>
                <p className="text-sm text-danger-fg font-medium mt-3">
                  This action cannot be undone. You'll need to re-import your subjects.
                </p>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Clear Assignments Confirmation Modal */}
      {showClearAssignmentsConfirm && <Modal isOpen={showClearAssignmentsConfirm} onClose={() => setShowClearAssignmentsConfirm(false)} title="Clear All Assignments" footer={<>
              <Button variant="secondary" onClick={() => setShowClearAssignmentsConfirm(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleClearAllAssignments} className="bg-danger-solid hover:bg-danger-solid">
                Clear All Assignments
              </Button>
            </>}>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-6 w-6 text-danger-fg flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-medium text-ink-900">
                  Are you sure you want to clear all assignments?
                </h3>
                <p className="text-sm text-ink-500 mt-2">
                  This will remove all teacher assignments from "{grid.name}":
                </p>
                <ul className="text-sm text-ink-500 mt-1 list-disc pl-5 space-y-1">
                  <li>{gridStats.totalAssignments} teacher assignments will be removed</li>
                  <li>All teaching loads will be reset to zero</li>
                  <li>The grid structure and classes will remain intact</li>
                  <li>Only the assignments (teacher-to-class links) will be cleared</li>
                </ul>
                <p className="text-sm text-danger-fg font-medium mt-3">
                  This action cannot be undone. You will need to reassign teachers manually or use auto-assignment again.
                </p>
              </div>
            </div>
          </div>
        </Modal>}
    </>;
};
export default GridSettingsModal;